import React from 'react';
import {
  Database, BarChart3, TrendingUp, Target,
  Zap, Shield, FileText,
} from 'lucide-react';
import ServicePage from '../../components/enterprise/ServicePage';

const CleanEnergyDataAnalysisPage = () => (
  <ServicePage
    hero={{
      eyebrow: 'Clean Energy Data Collection & Analysis',
      title: (
        <>
          Use proven methods <em>to collect and clean data</em>
        </>
      ),
      lede: 'Use proven methods to collect and clean data from renewable assets, providing actionable insights for operational excellence in clean energy operations.',
      image: '/assets/img/general/steptodown.com341684.jpg',
      imageAlt: 'Renewable generation site producing operational data',
      actions: [
        { label: 'Start data analysis', to: '/contact' },
        { label: 'View case studies', to: '/case-studies' },
      ],
      metrics: [
        { value: '99.9%', label: 'Data accuracy' },
        { value: '60%', label: 'Processing speed' },
        { value: '25%', label: 'Efficiency gain' },
        { value: '24/7', label: 'Data processing' },
      ],
    }}
    overview={{
      eyebrow: 'Overview',
      title: 'Advanced data capabilities',
      lede: 'Comprehensive data collection and analysis tools designed for clean energy operations.',
      checklist: [
        'Improve operational efficiency by 25%',
        'Reduce data processing time by 60%',
        'Increase data accuracy by 40%',
        'Enable data-driven decision making',
      ],
      image: '/assets/img/general/solar-cells-4518932_1280.jpg',
      imageAlt: 'Solar cells generating measurement data',
      imageCaption: 'Clean, validated data as the foundation for every operational decision',
    }}
    capabilities={{
      eyebrow: 'Key features & benefits',
      title: 'Built for clean energy operations',
      lede: 'Comprehensive data collection and analysis designed to optimize clean energy operations.',
      items: [
        {
          icon: Database,
          title: 'Data collection',
          description: 'Proven methods to collect and clean data from renewable assets across your portfolio.',
        },
        {
          icon: BarChart3,
          title: 'Advanced analysis',
          description: 'Comprehensive data analysis providing actionable insights for operational excellence.',
        },
        {
          icon: TrendingUp,
          title: 'Performance insights',
          description: 'Deep insights into asset performance patterns and optimization opportunities.',
        },
        {
          icon: Target,
          title: 'Actionable recommendations',
          description: 'Data-driven recommendations to improve efficiency and reduce operational costs.',
        },
      ],
    }}
    process={{
      eyebrow: 'Our data collection process',
      title: 'A systematic approach',
      lede: 'A systematic approach to collecting, cleaning, and analyzing clean energy data.',
      steps: [
        {
          title: 'Collection',
          description: 'Automated collection of data from renewable assets using proven methods and protocols.',
        },
        {
          title: 'Cleaning & validation',
          description: 'Advanced algorithms clean and validate data to ensure accuracy and reliability.',
        },
        {
          title: 'Analysis',
          description: 'Comprehensive analysis providing actionable insights for operational excellence.',
        },
      ],
    }}
    outcomes={{
      eyebrow: 'Data capabilities',
      title: 'What the data layer delivers',
      items: [
        {
          icon: Zap,
          title: 'Real-time data processing',
          description: 'Process and analyze data in real-time for immediate insights.',
        },
        {
          icon: Shield,
          title: 'Data quality assurance',
          description: 'Automated data cleaning and validation processes.',
        },
        {
          icon: TrendingUp,
          title: 'Predictive analytics',
          description: 'Advanced analytics to predict future performance trends.',
        },
        {
          icon: FileText,
          title: 'Custom reporting',
          description: 'Generate customized reports tailored to your specific needs.',
        },
      ],
    }}
    cta={{
      title: 'Ready to optimize your data operations?',
      body: 'Discover how our data collection and analysis solution can improve your clean energy operations.',
      primary: { label: 'Start data analysis', to: '/contact' },
      secondary: { label: 'View case studies', to: '/case-studies' },
    }}
  />
);

export default CleanEnergyDataAnalysisPage;
